import { useState, useEffect, useCallback } from 'react';

// Key must stay in sync with the anti-flash inline script in /index.html.
const STORAGE_KEY = 'user_theme';
const DARK_CLASS = 'dark';

// SINGLE-CONSUMER ASSUMPTION
// Each useTheme() call gets its own useState — there is no shared store. The
// app currently has exactly one consumer (ThemeToggle). If you add a second,
// the two instances will desync (one's toggle will only update its own state).
// Migrate to useSyncExternalStore + a module-level subscriber set before
// adding additional consumers.

function readInitialTheme() {
  // INTENTIONAL: default to light. Dark mode is an opt-in via the header
  // toggle, NOT auto-applied from OS prefers-color-scheme. Once the dark
  // theme is stable across every page, switch this back to honour the OS
  // pref on first visit (see git blame for the previous implementation).
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored === 'dark';
  } catch (e) {
    // localStorage may be unavailable (private mode); fall through to light.
  }
  return false;
}

function persist(isDark) {
  try {
    localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');
  } catch (e) {
    // ignore; class is still applied so theme works for this session
  }
}

export default function useTheme() {
  const [isDark, setIsDark] = useState(readInitialTheme);

  useEffect(() => {
    document.documentElement.classList.toggle(DARK_CLASS, isDark);
  }, [isDark]);

  const toggle = useCallback(() => {
    setIsDark((prev) => {
      const next = !prev;
      persist(next);
      return next;
    });
  }, []);

  return { isDark, toggle };
}
